import { render } from 'preact';
import { App } from './app';
import './styles.css';

const root = document.getElementById('root');
if (root) {
  render(<App />, root);
}

/**
 * Reports our rendered height to the embedding page so `embed.js` can size the
 * iframe. An iframe has no content-driven height of its own, so without this the
 * widget sits in a fixed box and either clips a long class or leaves a gap under
 * a short one.
 *
 * Two things make this harder than "observe and post":
 *
 * 1. Measure `#root`, never `documentElement`. Inside an iframe the root element
 *    is stretched to the frame's height, so measuring it and then resizing the
 *    frame to match is circular — the parent shrinks the frame, the next
 *    observation reports the shrunken height, and the widget collapses.
 *
 * 2. Resizing the frame is itself a resize, so a report and the layout it causes
 *    race. Observed while testing two embeds on one page: one frame settled at
 *    its full height and an identical one stayed stuck at the loading
 *    placeholder's 67px, because its second ResizeObserver delivery never came.
 *    So a report is not trusted until the measurement repeats later; each
 *    observation starts a short settle loop that re-measures until the height
 *    holds steady, which converges instead of depending on one callback.
 *
 * The settle loop uses timers rather than requestAnimationFrame on purpose: an
 * embed is routinely below the fold or in a background tab, where rAF is
 * throttled to nothing and the widget would never reach its final height.
 */
const SETTLE_INTERVAL_MS = 50;
const SETTLE_REPEATS = 6;

function startHeightReporting(element: HTMLElement): void {
  if (window.parent === window) return;

  let lastReported = -1;
  let stableTicks = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const tick = (): void => {
    timer = null;
    const height = Math.ceil(element.getBoundingClientRect().height);

    if (height > 0) {
      if (height !== lastReported) {
        lastReported = height;
        // Round up: a fractional height rounds down to a 1px clip at the bottom.
        window.parent.postMessage({ type: 'ed-results-widget:height', height }, '*');
        stableTicks = 0;
      } else {
        stableTicks += 1;
      }
    }

    // Keep re-measuring until the height has held steady for a few ticks. That
    // covers the frame being resized underneath us and any late reflow (font
    // swap, scrollbar appearing) without polling forever.
    if (stableTicks < SETTLE_REPEATS) {
      timer = setTimeout(tick, SETTLE_INTERVAL_MS);
    }
  };

  const restart = (): void => {
    stableTicks = 0;
    if (!timer) timer = setTimeout(tick, 0);
  };

  new ResizeObserver(restart).observe(element);
  window.addEventListener('load', restart);
  restart();
}

if (root) {
  startHeightReporting(root);
}
